import React, { useState, useEffect } from 'react';
import { api } from '../../utils/requestInterceptor';
import axios from 'axios';
import { toast } from 'react-hot-toast';

function ProjectFileUploader({ projectId, token, onFileUploaded }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add state for tracking downloading files
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  
  useEffect(() => {
    if (projectId) {
      fetchFiles();
    }
  }, [projectId]);
  
  const fetchFiles = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/files/project-files/${encodeURIComponent(token)}/${projectId}`);
      
      if (response.data && response.data.files) {
        setFiles(response.data.files);
      }
    } catch (error) {
      console.error('Error fetching project files:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Reset any previous errors
      setErrorMessage('');
      
      // Check file size limit (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage('File size exceeds the 10MB limit.');
        return;
      }
      
      setSelectedFile(file);
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      console.log('Uploading file to project:', projectId);
      const uploadUrl = `/files/client-project-upload/${encodeURIComponent(token)}/${projectId}`;
      console.log('Upload URL:', uploadUrl);
      
      const response = await api.post(
        uploadUrl, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data && response.data.file) {
        // Add new file to the list
        setFiles([response.data.file, ...files]);
        
        // Notify parent component about the uploaded file
        if (onFileUploaded) {
          onFileUploaded(response.data.file);
        }
        
        // Reset the file input
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('File upload error:', error);
      console.error('File upload error response:', error.response?.data);
      console.error('File upload error status:', error.response?.status);
      setErrorMessage(error.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  // Add function to handle file downloads
  const handleDownload = async (fileId, fileName) => {
    try {
      setDownloadingFileId(fileId);
      
      const response = await axios.get(`/api/files/download/${fileId}`);
      
      if (response.data && response.data.success && response.data.downloadUrl) {
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.setAttribute('download', response.data.filename || fileName);
        link.setAttribute('target', '_blank');
        
        // Trigger a click on this link to start the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up by removing the link
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } else {
        throw new Error('Invalid download response');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file. Please try again.');
    } finally {
      setDownloadingFileId(null);
    }
  };
  
  // Format file size to human readable
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Project Documents
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Upload and manage documents for this project
        </p>
      </div>
      
      <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
        <div className="mb-6">
          <div className="flex items-center space-x-2">
            <label className="flex-1">
              <input
                type="file"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              <div className="px-4 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 text-center">
                {selectedFile ? selectedFile.name : 'Choose File'}
              </div>
            </label>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0`}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          
          {/* Progress bar during upload */}
          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
          
          {/* Error message */}
          {errorMessage && (
            <p className="mt-2 text-sm text-red-600">
              {errorMessage}
            </p>
          )}
        </div>
        
        {/* File list */}
        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Uploaded Files</h4>
          
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-8 h-8 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
            </div>
          ) : files.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.map((file) => (
                    <tr key={file.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {file.original_filename}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(file.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button 
                          onClick={() => handleDownload(file.id, file.original_filename)} 
                          className="text-blue-600 hover:text-blue-900"
                          disabled={downloadingFileId === file.id}
                        >
                          {downloadingFileId === file.id ? 'Downloading...' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-md">
              <p className="text-gray-500">No files have been uploaded to this project yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectFileUploader; 